import type { FileNode } from '../protocol';
import type { SyncProfile } from './syncSettings';
import { listLocalFiles, writeLocalFile } from './localDirStore';

export interface SyncProgress {
  message: string;
}

export interface SyncCallbacks {
  listHostDir: (relPath: string) => Promise<FileNode>;
  uploadToHost: (hostRelPath: string, file: File) => Promise<void>;
  downloadFromHost: (hostRelPath: string) => Promise<Blob>;
  onProgress: (p: SyncProgress) => void;
}

async function collectHostFiles(
  listHostDir: (rel: string) => Promise<FileNode>,
  startRel: string
): Promise<Map<string, { size: number; modifiedMs?: number }>> {
  const out = new Map<string, { size: number; modifiedMs?: number }>();
  async function walk(rel: string): Promise<void> {
    const node = await listHostDir(rel);
    for (const child of node.children ?? []) {
      if (child.kind === 'dir') {
        await walk(child.path);
      } else {
        out.set(child.path.replace(/\\/g, '/'), {
          size: child.size ?? 0,
          modifiedMs: child.modifiedMs,
        });
      }
    }
  }
  await walk(startRel);
  return out;
}

function hostBase(profile: SyncProfile): string {
  return profile.hostRelDir.trim().replace(/\\/g, '/').replace(/\/$/, '');
}

function syncTreePrefix(profile: SyncProfile): string {
  const base = hostBase(profile);
  const folder = profile.localDirName.replace(/\\/g, '/');
  return base ? `${base}/${folder}` : folder;
}

function hostPathForLocal(profile: SyncProfile, localRel: string): string {
  const prefix = syncTreePrefix(profile);
  const local = localRel.replace(/\\/g, '/');
  return local ? `${prefix}/${local}` : prefix;
}

function localRelFromHost(profile: SyncProfile, hostRel: string): string {
  const prefix = syncTreePrefix(profile);
  const norm = hostRel.replace(/\\/g, '/');
  if (norm === prefix) return '';
  if (norm.startsWith(`${prefix}/`)) return norm.slice(prefix.length + 1);
  return '';
}

export type SyncMode = 'initial' | 'incremental';

/** Push local → host. Initial uploads everything; incremental skips unchanged files. */
export async function runProfileUpload(
  profile: SyncProfile,
  localRoot: FileSystemDirectoryHandle,
  mode: SyncMode,
  cb: SyncCallbacks
): Promise<void> {
  cb.onProgress({ message: 'Scanning this device…' });
  const localFiles = await listLocalFiles(localRoot);
  let hostFiles = new Map<string, { size: number; modifiedMs?: number }>();
  if (mode === 'incremental') {
    cb.onProgress({ message: 'Checking host…' });
    hostFiles = await collectHostFiles(cb.listHostDir, hostBase(profile));
  }

  for (const [localRel, meta] of localFiles) {
    const hostRel = hostPathForLocal(profile, localRel);
    if (mode === 'incremental') {
      const hostMeta = hostFiles.get(hostRel);
      const needsUp =
        !hostMeta ||
        hostMeta.size !== meta.size ||
        (hostMeta.modifiedMs !== undefined && meta.modifiedMs > hostMeta.modifiedMs + 2000);
      if (!needsUp) continue;
    }
    cb.onProgress({ message: `Uploading ${localRel}` });
    const file = await meta.handle.getFile();
    await cb.uploadToHost(hostRel, file);
  }

  cb.onProgress({ message: 'Upload to host complete' });
}

/** Pull host → local. Initial downloads everything under the job destination; incremental skips matching local files. */
export async function runProfileDownload(
  profile: SyncProfile,
  localRoot: FileSystemDirectoryHandle,
  mode: SyncMode,
  cb: SyncCallbacks
): Promise<void> {
  cb.onProgress({ message: 'Scanning host…' });
  const hostFiles = await collectHostFiles(cb.listHostDir, hostBase(profile));
  const localFiles =
    mode === 'incremental' ? await listLocalFiles(localRoot) : new Map();

  for (const [hostRel, hostMeta] of hostFiles) {
    const localRel = localRelFromHost(profile, hostRel);
    if (!localRel) continue;
    if (mode === 'incremental') {
      const localMeta = localFiles.get(localRel);
      if (localMeta && localMeta.size === hostMeta.size) continue;
    }
    cb.onProgress({ message: `Downloading ${localRel}` });
    const blob = await cb.downloadFromHost(hostRel);
    await writeLocalFile(localRoot, localRel, blob);
  }

  cb.onProgress({ message: 'Download from host complete' });
}

/** Runs a job according to its configured sync type (incremental or initial). */
export async function runProfileJob(
  profile: SyncProfile,
  localRoot: FileSystemDirectoryHandle,
  mode: SyncMode,
  cb: SyncCallbacks
): Promise<void> {
  await runProfileUpload(profile, localRoot, mode, cb);
  if (profile.syncType === 'two-way') {
    await runProfileDownload(profile, localRoot, mode, cb);
  }
}

/** @deprecated use runProfileUpload */
export const runProfileSync = runProfileUpload;
